import {
  calendarDateFromUtcDay as fromUtcDay,
  formatDateRange,
  formatDayMonth,
  formatWeekdayDayMonth,
  parseLocalDateTime,
  startOfDay,
  toDateOnly,
  weekdayShort,
} from '@voll-intelligence/dates';

/*
 * As datas do documento de viagem sobre o módulo de datas do intelligence-ui-kit
 * (#4124): nomes de mês e dia da semana em tabelas fixas por idioma, e a régua
 * ("Seg, 14 set", "10–17 nov", "28 dez – 3 jan 2027") num lugar só.
 */

const DAY_MS = 86_400_000;

/** `monthShort`: o mês abreviado na caixa do próprio idioma, sem ponto ("set",
 *  "Sep", "sept", #3990). */
export { monthShort, startOfDay } from '@voll-intelligence/dates';

/** Rótulos curtos dos 7 dias da semana (Dom..Sáb) no locale ativo. */
export function weekdayShortLabels(locale: string): string[] {
  // 2023-01-01 é um domingo → índice 0..6 = Dom..Sáb.
  return Array.from({ length: 7 }, (_, i) => weekdayShort(new Date(2023, 0, 1 + i), locale));
}

/** Data-só guardada à meia-noite UTC → o MESMO dia no calendário local. Sem isso
 *  um fuso negativo (America/Sao_Paulo) renderiza o dia anterior. */
export function calendarDateFromUtcDay(date: Date | null): Date | null {
  return date ? fromUtcDay(date) : null;
}

/** Faixa compacta: "17 jun" (dia único), "20–22 jun" (mesmo mês), "29 jun – 2 jul"
 *  (meses diferentes). Vazio sem início.
 *
 *  Com `today`, o ano entra por extenso no fim quando alguma ponta cai fora do ano
 *  de `today` ("28 dez – 3 jan 2027"). Sem ele a faixa nunca leva ano: as telas da
 *  viagem e as listas mostram o período sem ano, e quem passa `today` (as buscas)
 *  é quem precisa distinguir o ano que vem. Parâmetro, não `new Date()` interno,
 *  para o teste fixar o ano corrente. */
export function formatTripRange(
  startAt: Date | null,
  endAt: Date | null,
  locale: string,
  today?: Date,
): string {
  return startAt ? formatDateRange(startAt, endAt, locale, today) : '';
}

/** Noites da viagem = dias entre início e fim. Null sem janela completa (ou 0). */
export function tripNights(startAt: Date | null, endAt: Date | null): number | null {
  if (!startAt || !endAt) return null;
  const n = Math.round((startOfDay(endAt).getTime() - startOfDay(startAt).getTime()) / DAY_MS);
  return n > 0 ? n : null;
}

/** "YYYY-MM-DD" do dia LOCAL de `d` — a escrita de volta de uma meia-noite local
 *  para string ISO de data. Nunca `toISOString().slice(0, 10)`: aquele caminho
 *  converte para UTC e, em fuso negativo (America/Sao_Paulo), imprime o dia
 *  ANTERIOR. Par de `calendarDateFromUtcDay`, que faz a leitura no outro sentido. */
export function isoDateLocal(d: Date): string {
  return toDateOnly(d);
}

const TIME_FMT = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'UTC',
});

/** Parseia um ISO local-naive (offset-less, "2026-07-10T05:35:00") como horário
 *  LOCAL (wall-clock do aeroporto). Null se ausente/ilegível. */
export function parseLocalIso(s: string | null | undefined): Date | null {
  return parseLocalDateTime(s);
}

/** "HH:MM" do wall-clock: usa a string local-naive quando presente (verbatim,
 *  sem timezone), senão formata o instante UTC de fallback em 24h. */
export function localHHMM(local: string | null | undefined, fallback: Date | null): string {
  const m = local ? /T(\d{2}):(\d{2})/.exec(local) : null;
  if (m) return `${m[1] ?? ''}:${m[2] ?? ''}`;
  return fallback ? TIME_FMT.format(fallback) : '';
}

/** O wall-clock como `Date` local — o par de `localHHMM`, com a MESMA leitura: a
 *  string local-naive quando presente, senão o relógio UTC do instante de fallback.
 *  Sem isso o dia sai do fuso do runtime e a hora do UTC: a data-só à meia-noite
 *  UTC (o ônibus pedido à equipe, sem horário) caía no dia ANTERIOR em
 *  America/Sao_Paulo, com "00:00" ao lado. */
export function localWallClock(
  local: string | null | undefined,
  fallback: Date | null,
): Date | null {
  return parseLocalIso(local) ?? parseLocalIso(fallback?.toISOString());
}

/** "Ter, 25 ago" — o dia como o documento escreve; no eyebrow o CSS o passa por
 *  `uppercase`. Vírgula obrigatória: é o léxico da peça. O rótulo vem de
 *  `weekdayShortLabels` (caixa do locale), não de `weekdayShort` (caixa alta), que
 *  no meio de uma frase gritaria só o dia da semana. `today` tem a regra do ano de
 *  `formatTripRange`. */
export function dayLabel(date: Date, locale: string, today?: Date): string {
  return formatWeekdayDayMonth(date, locale, today);
}

/** "Seg 3 jun" — o cabeçalho de um dia do itinerário. */
export function dayHeading(date: Date, locale: string): string {
  return `${weekdayShort(date, locale)} ${formatDayMonth(date, locale)}`;
}

/** "24 ago – 4 set" / "24 ago" quando as duas pontas caem no mesmo dia. */
export function periodLabel(from: Date, to: Date | null, locale: string): string {
  const start = formatDayMonth(from, locale);
  if (!to || to.toDateString() === from.toDateString()) return start;
  return `${start} – ${formatDayMonth(to, locale)}`;
}

/** "UTC−3" para um IANA numa data — o offset REAL daquele dia, com horário de verão
 *  já resolvido pelo ICU. */
export function utcOffset(zone: string, at: Date): string | null {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName')?.value;
  return part ? part.replace('GMT', 'UTC').replace('-', '−') : null;
}
